import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { Card, CardHeader } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Avatar } from '../../components/common/Avatar';
import { Input, Textarea } from '../../components/common/Input';
import { Loading } from '../../components/common/Loading';
import { getUserProfile, updateUserProfile } from '../../api/users';
import { updateTenantCustomization } from '../../api/tenant';
import type { UserProfile } from '../../types/user';

interface ProfileProps {
  tenant: string;
}

export function Profile({ tenant }: ProfileProps) {
  const { user, setUser } = useAuthStore();
  const { config, loadConfig } = useTenantStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingCustomization, setIsSavingCustomization] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [customizationSuccess, setCustomizationSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    timezone: '',
  });

  const [dashboardLabel, setDashboardLabel] = useState('');
  const [coachDmHighlightColor, setCoachDmHighlightColor] = useState('#dbeafe');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await getUserProfile(tenant);
        setProfile(data.profile);
        setFormData({
          name: data.user.name,
          bio: data.profile?.bio || '',
          timezone: data.profile?.timezone || '',
        });
        setDashboardLabel(config?.customization?.dashboardLabel || 'Coach');
        setCoachDmHighlightColor(config?.customization?.coachDmHighlightColor || '#dbeafe');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [tenant, config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const data = await updateUserProfile(tenant, {
        name: formData.name,
        bio: formData.bio || undefined,
        timezone: formData.timezone || undefined,
      });

      setProfile(data.profile);
      setUser(data.user);
      setSuccess(true);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCustomization = async () => {
    setIsSavingCustomization(true);
    setCustomizationSuccess(false);

    try {
      await updateTenantCustomization(tenant, {
        dashboardLabel: dashboardLabel || undefined,
        coachDmHighlightColor: coachDmHighlightColor || undefined,
      });
      await loadConfig(tenant);
      setCustomizationSuccess(true);

      setTimeout(() => setCustomizationSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customization');
    } finally {
      setIsSavingCustomization(false);
    }
  };

  if (isLoading) {
    return <Loading message="Loading profile..." />;
  }

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader
          title="Profile"
          description="Manage your personal information"
        />

        <div className="flex items-center gap-4 mb-6">
          <Avatar src={user.avatarUrl} name={user.name} size="xl" />
          <div>
            <p className="font-medium text-gray-900">{user.name}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
            <p className="text-xs text-gray-400 mt-1">
              Signed in with {user.avatarUrl ? 'Google' : 'Facebook'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            Profile updated successfully!
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Display Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <Textarea
            label="Bio"
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            rows={3}
            placeholder="Tell us about yourself..."
          />

          <Input
            label="Timezone"
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            placeholder="e.g., America/New_York"
            helpText="Your timezone helps us schedule at the right time"
          />

          <div className="pt-4">
            <Button type="submit" isLoading={isSaving}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>

      {(user.role === 'coach' || user.role === 'admin') && (
        <Card>
          <CardHeader
            title="Customization"
            description="Customize labels and terminology"
          />

          {customizationSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              Customization updated successfully!
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Dashboard Label"
              value={dashboardLabel}
              onChange={(e) => setDashboardLabel(e.target.value)}
              placeholder="e.g., Coach, Teacher, Mentor"
              helpText="Customize the name for the coach/dashboard section (defaults to 'Coach')"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coach DM Highlight Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={coachDmHighlightColor}
                  onChange={(e) => setCoachDmHighlightColor(e.target.value)}
                  className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={coachDmHighlightColor}
                  onChange={(e) => setCoachDmHighlightColor(e.target.value)}
                  placeholder="#dbeafe"
                  className="flex-1"
                />
                <div
                  className="w-10 h-10 rounded border border-gray-300"
                  style={{ backgroundColor: coachDmHighlightColor }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Background color for highlighting direct messages with coaches in user feeds
              </p>
            </div>

            <div className="pt-4">
              <Button onClick={handleSaveCustomization} isLoading={isSavingCustomization}>
                Save Customization
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Account"
          description="Manage your account settings"
        />

        <div className="space-y-4">
          <div className="flex justify-between items-center py-3 border-b">
            <div>
              <p className="font-medium text-gray-900">Email</p>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>

          <div className="flex justify-between items-center py-3">
            <div>
              <p className="font-medium text-gray-900">Role</p>
              <p className="text-sm text-gray-500 capitalize">{user.role}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
